/**
 * User interface preferences: theme, table density, sidebar state.
 *
 * Persisted to localStorage and applied to <html> so the choice survives reloads —
 * the theme is additionally applied by an inline script in index.html before first
 * paint, so a dark-mode user never sees a white flash.
 */

import * as React from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

interface PreferencesValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  /** The theme actually in effect, with `system` already resolved. */
  resolvedTheme: 'light' | 'dark';
  density: Density;
  setDensity: (density: Density) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
}

const PreferencesContext = React.createContext<PreferencesValue | null>(null);

const KEYS = {
  theme: 'ias.theme',
  density: 'ias.density',
  sidebar: 'ias.sidebar',
} as const;

function readStored<T extends string>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  return (localStorage.getItem(key) as T | null) ?? fallback;
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<ThemeMode>(() => readStored(KEYS.theme, 'system'));
  const [density, setDensityState] = React.useState<Density>(() =>
    readStored(KEYS.density, 'comfortable'),
  );
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(
    () => readStored<string>(KEYS.sidebar, 'false') === 'true',
  );
  const [systemDark, setSystemDark] = React.useState(systemPrefersDark);

  // Following the OS setting means reacting to it changing, not just reading it once.
  React.useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedTheme === 'dark');
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setTheme = React.useCallback((next: ThemeMode) => {
    setThemeState(next);
    // `system` is stored as the resolved value so the pre-paint script in index.html,
    // which cannot evaluate "system", still picks the right one on the next load.
    localStorage.setItem(KEYS.theme, next === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : next);
    if (next === 'system') localStorage.removeItem(KEYS.theme);
  }, []);

  const setDensity = React.useCallback((next: Density) => {
    setDensityState(next);
    localStorage.setItem(KEYS.density, next);
  }, []);

  const toggleSidebar = React.useCallback(() => {
    setSidebarCollapsed((current) => {
      localStorage.setItem(KEYS.sidebar, String(!current));
      return !current;
    });
  }, []);

  const value = React.useMemo<PreferencesValue>(
    () => ({ theme, setTheme, resolvedTheme, density, setDensity, sidebarCollapsed, toggleSidebar }),
    [theme, setTheme, resolvedTheme, density, setDensity, sidebarCollapsed, toggleSidebar],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesValue {
  const context = React.useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return context;
}
